import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from 'react-query';
import { fetchIntegrationPiecesGestionStats } from 'utils/ts-queries';
import {
    IntegrationPiecesGestionEntite,
    IntegrationPiecesGestionEntiteStats,
    IntegrationPiecesGestionPeriod
} from 'types/integrationPiecesGestion';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
    blue,
    green,
    orange,
    purple,
    red,
    teal
} from '@mui/material/colors';

ChartJS.register(
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

type ScaleType = 'linear' | 'logarithmic';
type TypeDonnee = 'integrations' | 'modifications';
type ModeAffichage = TypeDonnee | 'both';

// Par entité : teinte pleine pour les intégrations, teinte claire pour les
// modifications (empilées juste au-dessus des intégrations de la même entité).
const SERIES: Array<{
    key: IntegrationPiecesGestionEntite;
    label: string;
    color: string;
    colorClair: string;
}> = [
    { key: 'commandeGestion', label: 'Commandes', color: blue[500], colorClair: blue[200] },
    { key: 'bonLivraisonGestion', label: 'Bons de livraison', color: green[500], colorClair: green[200] },
    { key: 'factureGestion', label: 'Factures', color: orange[500], colorClair: orange[200] },
    { key: 'demandeTransfertGestion', label: 'Demandes de transfert', color: purple[500], colorClair: purple[200] },
    { key: 'litigeGestion', label: 'Litiges', color: red[500], colorClair: red[200] },
    { key: 'reclamationSurBonDeVente', label: 'Réclamations sur bon de vente', color: teal[500], colorClair: teal[200] }
];

function AdministrationIntegrationPiecesGestion() {
    const [period, setPeriod] = useState<IntegrationPiecesGestionPeriod>('week');
    const [scale, setScale] = useState<ScaleType>('linear');
    const [mode, setMode] = useState<ModeAffichage>('both');

    // Séries masquées via la légende (clé : `${entite}|${type}`), à la façon
    // de la légende chart.js native (désactivée au profit de la table).
    const [seriesMasquees, setSeriesMasquees] = useState<Set<string>>(new Set());

    const cleSerie = (entite: IntegrationPiecesGestionEntite, type: TypeDonnee) => `${entite}|${type}`;

    const toggleSerie = (entite: IntegrationPiecesGestionEntite, type: TypeDonnee) => {
        setSeriesMasquees(prev => {
            const next = new Set(prev);
            const cle = cleSerie(entite, type);
            if (next.has(cle)) {
                next.delete(cle);
            } else {
                next.add(cle);
            }
            return next;
        });
    };

    // Types de données affichés selon le mode (dans l'ordre d'empilement).
    const typesAffiches: TypeDonnee[] = mode === 'both' ? ['integrations', 'modifications'] : [mode];

    // Clic sur l'entête d'entité : bascule toutes les séries affichées de l'entité d'un coup.
    const toggleEntite = (entite: IntegrationPiecesGestionEntite) => {
        setSeriesMasquees(prev => {
            const next = new Set(prev);
            const cles = typesAffiches.map(t => cleSerie(entite, t));
            const toutMasque = cles.every(c => next.has(c));
            cles.forEach(c => (toutMasque ? next.delete(c) : next.add(c)));
            return next;
        });
    };

    // Instant de référence commun aux appels : ancre la fenêtre glissante côté serveur
    // pour que les buckets des 6 entités soient alignés (fixé au montage de l'écran).
    const [reference] = useState(() => new Date().toISOString());

    // Conteneur scrollable du graphique en mode log (fenêtre visible ~2 jours).
    const scrollRef = useRef<HTMLDivElement>(null);

    // Axe Y "sticky" en mode log : canvas superposé à gauche du conteneur scrollable,
    // dans lequel un plugin recopie la zone de l'axe du graphique après chaque rendu.
    // Le scroll ne déplaçant que le grand canvas, la copie reste visible.
    // Le plugin est passé en permanence au chart (react-chartjs-2 n'enregistre les
    // plugins qu'à la création) ; il ne fait rien tant que le canvas overlay n'est
    // pas monté, c'est-à-dire hors mode log.
    const axeYCanvasRef = useRef<HTMLCanvasElement>(null);
    const axeYStickyPlugin = useMemo(() => ({
        id: 'axeYSticky',
        afterDraw: (chart: ChartJS) => {
            const overlay = axeYCanvasRef.current;
            const yScale = chart.scales['y'];
            if (!overlay || !yScale) {
                return;
            }
            const dpr = ((chart as unknown as { currentDevicePixelRatio?: number }).currentDevicePixelRatio)
                ?? window.devicePixelRatio ?? 1;
            const largeurCss = yScale.right + 2;
            const hauteurCss = chart.height;
            overlay.width = Math.round(largeurCss * dpr);
            overlay.height = Math.round(hauteurCss * dpr);
            overlay.style.width = `${largeurCss}px`;
            overlay.style.height = `${hauteurCss}px`;
            const ctx = overlay.getContext('2d');
            if (!ctx) {
                return;
            }
            // Fond opaque pour masquer les barres qui défilent dessous
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, overlay.width, overlay.height);
            ctx.drawImage(chart.canvas, 0, 0, overlay.width, overlay.height, 0, 0, overlay.width, overlay.height);
        }
    }), []);
    const chartPlugins = useMemo(() => [axeYStickyPlugin], [axeYStickyPlugin]);

    // Un appel par entité : les séries s'affichent au fil de l'eau.
    const results = useQueries(
        SERIES.map(s => ({
            queryKey: ['integrationPiecesGestionStats', s.key, period, reference],
            queryFn: () => fetchIntegrationPiecesGestionStats(s.key, period, reference),
            refetchOnWindowFocus: false
        }))
    );

    const handlePeriodChange = (
        _event: React.MouseEvent<HTMLElement>,
        next: IntegrationPiecesGestionPeriod | null
    ) => {
        if (next !== null) {
            setPeriod(next);
        }
    };

    const handleScaleChange = (
        _event: React.MouseEvent<HTMLElement>,
        next: ScaleType | null
    ) => {
        if (next !== null) {
            setScale(next);
        }
    };

    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        next: ModeAffichage | null
    ) => {
        if (next !== null) {
            setMode(next);
        }
    };

    const isLog = scale === 'logarithmic';

    const loaded = results
        .map(r => r.data)
        .filter((d): d is IntegrationPiecesGestionEntiteStats => d !== undefined);
    const isFetching = results.some(r => r.isLoading);
    const seriesEnErreur = SERIES.filter((_, i) => results[i]?.isError).map(s => s.label);
    const labels = loaded[0]?.labels ?? [];

    // En log (barres côte à côte), le graphique s'étale au-delà du panneau avec un
    // scroll horizontal : la fenêtre visible correspond à ~2 jours en vue semaine.
    const pxParBucket = period === 'week' ? 28 : 90;
    const largeurLog = Math.max(labels.length * pxParBucket, 800);

    // Au passage en log (ou changement de période/mode), on se positionne sur
    // les données les plus récentes (à droite).
    useEffect(() => {
        if (isLog && scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
        }
    }, [isLog, period, mode, labels.length]);

    // Pas de useMemo : le composant ne re-rend que sur changement d'état pertinent
    // (toggle, arrivée d'une série) et la construction est triviale.
    // Un dataset par entité et par type affiché, intégrations puis modifications :
    // l'ordre des datasets détermine l'empilement, les modifications se posent
    // donc juste au-dessus des intégrations de la même entité.
    const chartData = {
        labels,
        datasets: SERIES.flatMap(s => {
            const stats = loaded.find(d => d.entite === s.key);
            if (stats === undefined) {
                return [];
            }
            return typesAffiches.map(type => ({
                label: type === 'integrations' ? s.label : `${s.label} (modif.)`,
                data: stats[type],
                backgroundColor: type === 'integrations' ? s.color : s.colorClair,
                borderColor: type === 'integrations' ? s.color : s.colorClair,
                borderWidth: 1,
                hidden: seriesMasquees.has(cleSerie(s.key, type))
            }));
        })
    };

    const options = useMemo(() => {
        const typeLabel = mode === 'integrations'
            ? 'Intégrations'
            : mode === 'modifications' ? 'Modifications' : 'Intégrations et modifications';
        return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // Légende native désactivée : remplacée par la table sous le graphique
            legend: { display: false },
            title: {
                display: true,
                text: period === 'week'
                    ? `${typeLabel} par heure sur les 7 derniers jours`
                    : `${typeLabel} par jour sur les 30 derniers jours`,
                font: { family: 'Roboto', weight: '500' }
            },
            tooltip: { mode: 'index' as const, intersect: false },
            datalabels: {
                // Masqué en log (segments trop fins) ; sinon masque l'étiquette quand la valeur est 0
                display: !isLog,
                formatter: (value: number) => (value ? value : '')
            }
        },
        interaction: { mode: 'index' as const, intersect: false },
        scales: {
            // En log, les barres ne sont pas empilées mais côte à côte : l'empilement
            // sur un axe log est trompeur et écrase les petits flux.
            x: {
                stacked: !isLog,
                title: {
                    display: true,
                    text: period === 'week' ? 'Heure' : 'Jour'
                }
            },
            y: isLog
                ? {
                    type: 'logarithmic' as const,
                    stacked: false,
                    title: { display: true, text: 'Nombre (échelle log)' }
                }
                : {
                    stacked: true,
                    beginAtZero: true,
                    title: { display: true, text: 'Nombre' },
                    ticks: { precision: 0 }
                }
        }
        };
    }, [period, isLog, mode]);

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Intégration pièces gestion</Typography>

                <Stack direction="row" spacing={2}>
                    <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={handleModeChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="integrations">Intégrations</ToggleButton>
                        <ToggleButton value="modifications">Modifications</ToggleButton>
                        <ToggleButton value="both">Les deux</ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        value={scale}
                        exclusive
                        onChange={handleScaleChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="linear">Linéaire</ToggleButton>
                        <ToggleButton value="logarithmic">Log</ToggleButton>
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        value={period}
                        exclusive
                        onChange={handlePeriodChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="week">Semaine</ToggleButton>
                        <ToggleButton value="month">Mois</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Stack>

            <Paper sx={{ p: 2, height: 560, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {isFetching && (
                    <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
                )}

                {seriesEnErreur.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        Erreur lors du chargement : {seriesEnErreur.join(', ')}
                    </Alert>
                )}

                {loaded.length === 0 && isFetching && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                )}

                {loaded.length > 0 && (
                    <>
                        <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                            <Box
                                ref={scrollRef}
                                sx={{ height: '100%', overflowX: isLog ? 'auto' : 'hidden', overflowY: 'hidden' }}
                            >
                                <Box sx={{ height: '100%', ...(isLog ? { width: largeurLog, minWidth: '100%' } : {}) }}>
                                    <Bar data={chartData} options={options} plugins={chartPlugins} />
                                </Box>
                            </Box>

                            {/* Axe Y sticky (mode log) : copie de l'axe, insensible au scroll */}
                            {isLog && (
                                <canvas
                                    ref={axeYCanvasRef}
                                    style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                                />
                            )}
                        </Box>

                        {/* Légende en table : entêtes = entités (clic = bascule l'entité entière),
                            une ligne par type affiché (clic sur la pastille = bascule la série) */}
                        <Table size="small" sx={{ width: 'auto', mx: 'auto', mt: 1, '& td, & th': { border: 0, py: 0.25, px: 1.5 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    {SERIES.map(s => {
                                        const entiteMasquee =
                                            typesAffiches.every(t => seriesMasquees.has(cleSerie(s.key, t)));
                                        return (
                                            <TableCell
                                                key={s.key}
                                                align="center"
                                                onClick={() => toggleEntite(s.key)}
                                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 500,
                                                        opacity: entiteMasquee ? 0.4 : 1,
                                                        textDecoration: entiteMasquee ? 'line-through' : 'none'
                                                    }}
                                                >
                                                    {s.label}
                                                </Typography>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {typesAffiches.map(type => ({
                                    type,
                                    libelle: type === 'integrations' ? 'Intégrations' : 'Modifications'
                                })).map(ligne => (
                                    <TableRow key={ligne.type}>
                                        <TableCell>
                                            <Typography variant="caption">{ligne.libelle}</Typography>
                                        </TableCell>
                                        {SERIES.map(s => {
                                            const masquee = seriesMasquees.has(cleSerie(s.key, ligne.type));
                                            return (
                                                <TableCell
                                                    key={s.key}
                                                    align="center"
                                                    onClick={() => toggleSerie(s.key, ligne.type)}
                                                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 12,
                                                            borderRadius: 0.5,
                                                            display: 'inline-block',
                                                            backgroundColor: ligne.type === 'integrations' ? s.color : s.colorClair,
                                                            opacity: masquee ? 0.25 : 1
                                                        }}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </Paper>
        </Box>
    );
}

export default AdministrationIntegrationPiecesGestion;
